import {
  AspectRatio,
  Box,
  Button,
  DarkMode,
  Flex,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { CenteredContent } from "../../../../../components/CenteredContent";
import { Icon } from "../../../../../components/Icon";
import { Z_INDEX } from "../../../../../constants/zIndex";
import { Events } from "./Events";
import { MenuSearch } from "./Menu";
import { News } from "./News";
import { SearchResult } from "./Results";

/**
 * @typedef {Object} Props
 * @property {boolean} isSearchOpened
 * @property {import("react").Dispatch<import("react").SetStateAction<boolean>>} setIsSearchOpened
 */
/**
 * @param {Props} props
 */
export function HeaderSearch({ isSearchOpened, setIsSearchOpened }) {
  const handleClickSearch = useCallback(() => {
    setIsSearchOpened(false);
  }, [setIsSearchOpened]);

  return (
    <DarkMode>
      <Flex
        maxH="600px"
        flexDirection="column"
        role="dialog"
        aria-modal="true"
        aria-hidden={isSearchOpened ? "false" : "true"}
        visibility={isSearchOpened ? "visible" : "hidden"}
        pointerEvents={isSearchOpened ? "auto" : "none"}
        opacity={isSearchOpened ? 1 : 0}
        transition="all .25s ease"
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        backgroundColor="brandPrimary.700"
        zIndex={Z_INDEX.SEARCH_POPOVER}>
        <Box
          display={{ base: "none", lg: "block" }}
          backgroundColor="#fff"
          px="1rem">
          <CenteredContent>
            <Flex justify="flex-end">
              <Box backgroundColor="brandPrimary.700">
                <Button
                  onClick={handleClickSearch}
                  fontSize="16px"
                  fontWeight="400"
                  variant="ghost"
                  borderRadius={0}
                  rightIcon={<Icon icon="ms_close" size="24px" />}
                  pr=".5rem"
                  h="36px">
                  <Text as="span">
                    <FormattedMessage defaultMessage="Rechercher" />
                  </Text>
                </Button>
              </Box>
            </Flex>
          </CenteredContent>
        </Box>

        <Box px="1rem" overflowY="auto" willChange="transform" flex="1" pb="0">
          <CenteredContent>
            <Box py="1rem" w="192px">
              <AspectRatio ratio={192 / 80}>
                <Image
                  src={generateFrontPath({
                    id: "@front.internal-assets",
                    parameters: { filePath: "logo.svg" },
                    includeBasePath: true,
                  })}
                  alt="Logo"
                />
              </AspectRatio>
            </Box>

            <Text variant="h2" mt="1.5rem">
              <FormattedMessage defaultMessage="Votre ville à portée de clic" />
            </Text>

            <Box mt="1rem" maxW="850px">
              <SearchResult />

              <MenuSearch />
            </Box>

            <SimpleGrid columns={2} mt="2rem" gap="3rem">
              <Events />

              <News />
            </SimpleGrid>
          </CenteredContent>
        </Box>

        <Box
          height="2px"
          flexShrink={0}
          sx={{
            background:
              "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9))",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        />
      </Flex>
    </DarkMode>
  );
}
